import { useEffect, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

/** Component that executes the language check.
 * How it works:
 * 1. this one component calls useTranslation
 * 2. i18next isn't loaded, so the issues a freeze on the tree
 * 3. this component gets frozen
 * 4. TranslationUpdater has a Suspense that creates a barrier for that freeze, blocking the data stores from suspending.
 */
const Actor = ({
  onLanguageUpdate,
}: {
  onLanguageUpdate: (lang: string) => void;
}) => {
  const [, i18next] = useTranslation();
  const language = i18next?.language;
  useEffect(() => {
    onLanguageUpdate?.(language);
    // incorrect exhaustive-deps:
    // don't execute an effect that sets state upon the change
    // of the output of that state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);
  return null;
};

/** Execute a given function whenever the i18next language changes without suspending the tree.
 *> Note:
 *> This component is acting as a gateway layer between the React Suspense system that
 *> react-i18next uses and the normal react app flow. This is to allow our data contexts
 *> to listen to changes in language without getting suspended by the language library.
 */
export const TranslationUpdater = ({
  onLanguageUpdate,
}: {
  onLanguageUpdate?: (lang?: string) => void;
}) => (
  <Suspense fallback={null}>
    <Actor onLanguageUpdate={lang => onLanguageUpdate?.(lang)} />
  </Suspense>
);
